import { Card, SegmentedMeter, type MeterSegment } from "@bfc-software/pallet";
import type { DeliverSchemaTypes } from "@bfc-software/api-schemas";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import type { TFunction } from "i18next";

import { WORKLIST_KEY, type Worklist } from "../-data/filters";
import { formatCount, formatPercent } from "../-data/format";

import styles from "./summary-panel.module.css";

type Summary = DeliverSchemaTypes.components["schemas"]["DashSummary"];

type Props = {
  summary: Summary;
  /** The worklist standing in for the routes list, if any. */
  worklist: Worklist | undefined;
  /** Open a worklist, or close it when it is the one already open. */
  onWorklist: (worklist: Worklist | undefined) => void;
  lang: string;
  t: TFunction;
};

type Tone = "warning" | "danger";

function Kpi({
  label,
  value,
  tone,
  testRole,
}: {
  label: string;
  value: string;
  tone?: Tone;
  testRole: string;
}) {
  return (
    <div className={styles.kpi} data-test-role={testRole}>
      <span className={styles.kpiLabel}>{label}</span>
      <span className={`${styles.kpiValue} ${tone ? styles[tone] : ""}`}>
        {value}
      </span>
    </div>
  );
}

function MeterCard({
  title,
  headline,
  segments,
  total,
}: {
  title: string;
  headline: string;
  segments: ReadonlyArray<MeterSegment>;
  total?: number;
}) {
  return (
    <Card
      variant="panel"
      layout="body-only"
      body={
        <>
          <div className={styles.meterHead}>
            <span className={styles.meterTitle}>{title}</span>
            <span className={styles.meterHeadline}>{headline}</span>
          </div>
          <SegmentedMeter
            segments={segments}
            total={total}
            aria-label={`${title}: ${headline}`}
          />
        </>
      }
    />
  );
}

function WorklistCard({
  title,
  description,
  count,
  hasRows,
  viewLabel,
  tone,
  selected,
  onSelect,
  testRole,
}: {
  title: string;
  description: string;
  count: string;
  hasRows: boolean;
  viewLabel: string;
  tone?: Tone;
  selected: boolean;
  onSelect: () => void;
  testRole: string;
}) {
  return (
    <button
      type="button"
      className={`${styles.lane} ${selected ? styles.laneSelected : ""}`}
      aria-pressed={selected}
      // A count of nothing has nothing to open, unless it is already open.
      disabled={!hasRows && !selected}
      onClick={onSelect}
      data-test-role={testRole}
    >
      <Card
        variant="panel"
        layout="body-only"
        body={
          <>
            <div className={styles.meterHead}>
              <span className={styles.meterTitle}>{title}</span>
              <span
                className={`${styles.worklistCount} ${tone ? styles[tone] : ""}`}
              >
                {count}
              </span>
            </div>
            <span className={styles.description}>{description}</span>
            {hasRows ? (
              <span className={styles.laneCta}>{viewLabel}</span>
            ) : null}
          </>
        }
      />
    </button>
  );
}

function percentOf(done: number, total: number): number {
  return total > 0 ? (done / total) * 100 : 0;
}

export function SummaryPanel({
  summary,
  worklist,
  onWorklist,
  lang,
  t,
}: Props) {
  const n = (value: number) => formatCount(value, lang);
  const scanPct = formatPercent(summary.scanPct, lang) ?? t("none");
  const stopsPct =
    formatPercent(percentOf(summary.completedStops, summary.stops), lang) ??
    t("none");
  const notStarted = Math.max(
    summary.routes - summary.activeRoutes - summary.completedRoutes,
    0,
  );
  const returns = summary.currentReturns + summary.previousReturns;
  // Each card's number is the count of its own thing -- exceptions, pieces,
  // returns -- so it is not the row count of the list it opens.
  const worklists: ReadonlyArray<{
    id: Worklist;
    count: number;
    tone?: Tone;
  }> = [
    { id: "scan-exceptions", count: summary.scanExceptions, tone: "danger" },
    { id: "non-scanned", count: summary.piecesNonScanned, tone: "warning" },
    { id: "invoice-exceptions", count: summary.invoiceExceptions },
    { id: "returns", count: returns, tone: "warning" },
  ];

  return (
    <section className={styles.panel} data-test-role="deliver-summary">
      <div className={styles.kpis}>
        <Kpi
          testRole="kpi-routes"
          label={t("kpi.routes")}
          value={n(summary.routes)}
        />
        <Kpi
          testRole="kpi-active"
          label={t("kpi.active")}
          value={n(summary.activeRoutes)}
        />
        <Kpi
          testRole="kpi-completed"
          label={t("kpi.completed")}
          value={n(summary.completedRoutes)}
        />
        <Kpi
          testRole="kpi-stops"
          label={t("kpi.stopsDone")}
          value={`${n(summary.completedStops)} / ${n(summary.stops)}`}
        />
        <Kpi
          testRole="kpi-delivered"
          label={t("kpi.delivered")}
          value={n(summary.piecesDelivered)}
        />
        <Kpi
          testRole="kpi-scanned"
          label={t("kpi.scanned")}
          value={n(summary.piecesScanned)}
        />
        <Kpi
          testRole="kpi-non-scanned"
          label={t("kpi.nonScanned")}
          value={n(summary.piecesNonScanned)}
          tone="warning"
        />
        <Kpi testRole="kpi-scan-pct" label={t("kpi.scanPct")} value={scanPct} />
        <Kpi
          testRole="kpi-returns"
          label={t("kpi.returns")}
          value={t("kpi.returnsValue", {
            current: n(summary.currentReturns),
            previous: n(summary.previousReturns),
          })}
        />
        <Kpi
          testRole="kpi-avg-pph"
          label={t("kpi.avgPph")}
          value={n(summary.avgPph)}
        />
        <Kpi
          testRole="kpi-scan-exceptions"
          label={t("kpi.scanExceptions")}
          value={n(summary.scanExceptions)}
          tone="danger"
        />
        <Kpi
          testRole="kpi-invoice-exceptions"
          label={t("kpi.invoiceExceptions")}
          value={n(summary.invoiceExceptions)}
        />
      </div>

      <div className={styles.cards}>
        <MeterCard
          title={t("meters.scanQuality")}
          headline={scanPct}
          segments={[
            {
              value: summary.piecesScanned,
              tone: "good",
              label: t("meters.scanned", { count: summary.piecesScanned }),
            },
            {
              value: summary.piecesNonScanned,
              tone: "fair",
              label: t("meters.nonScanned", {
                count: summary.piecesNonScanned,
              }),
            },
          ]}
        />
        <MeterCard
          title={t("meters.routeStatus")}
          headline={`${n(summary.completedRoutes)} / ${n(summary.routes)}`}
          total={summary.routes}
          segments={[
            {
              value: summary.completedRoutes,
              tone: "progress",
              label: t("meters.routesCompleted", {
                count: summary.completedRoutes,
              }),
            },
            {
              value: summary.activeRoutes,
              tone: "good",
              label: t("meters.routesActive", { count: summary.activeRoutes }),
            },
            {
              value: notStarted,
              tone: "idle",
              label: t("meters.routesNotStarted", { count: notStarted }),
            },
          ]}
        />
        <MeterCard
          title={t("meters.stopsCompleted")}
          headline={stopsPct}
          total={summary.stops}
          segments={[
            {
              value: summary.completedStops,
              tone: "progress",
              label: t("meters.stopsDone", { count: summary.completedStops }),
            },
            {
              value: Math.max(summary.stops - summary.completedStops, 0),
              tone: "idle",
              legendOnly: true,
              label: t("meters.stopsRemaining", {
                count: Math.max(summary.stops - summary.completedStops, 0),
              }),
            },
          ]}
        />
      </div>

      <div className={styles.cards}>
        {worklists.map(({ id, count, tone }) => {
          const key = WORKLIST_KEY[id];
          return (
            <WorklistCard
              key={id}
              testRole={`worklist-${id}`}
              title={t(`worklists.${key}.title`)}
              description={t(`worklists.${key}.description`)}
              count={n(count)}
              hasRows={count > 0}
              viewLabel={t("worklists.view")}
              tone={tone}
              selected={worklist === id}
              onSelect={() => onWorklist(worklist === id ? undefined : id)}
            />
          );
        })}
      </div>
    </section>
  );
}
